<script setup lang="ts">
import { useButtons } from '~/components/BaseForm/compose/use-buttons'
import groupFields from '~/forms/house/virtualVisit/virtualVisitForm'

import type { HouseDetails } from '~/types/house/types'
import type { MutationType } from '../BaseForm/types'

const { t } = useI18n()
const { $api, $httpCookies } = useNuxtApp()
const { trackEvent } = useTracking()

const props = withDefaults(
  defineProps<{
    house: HouseDetails
    matterportVideoUrl?: string
    modelValue: boolean
  }>(),
  {
    matterportVideoUrl: '',
  },
)
defineEmits<{
  'update:model-value': [value: boolean]
}>()

const groupFieldsForm = reactive(groupFields())
const { btnPrimary } = useButtons()
btnPrimary.text = computed(() => t('house.virtualVisit.sendRequest'))
if (btnPrimary.attrs) {
  btnPrimary.attrs.blockFull = true
}

const initialData = reactive({
  firstname: '',
  lastname: '',
  email: '',
  phone: '',
  newsletter: false,
})

const success = ref(false)

const disabledButton = computed(() => {
  return (
    !initialData.firstname ||
    !initialData.lastname ||
    !initialData.email ||
    !initialData.phone
  )
})

const formattedPayload = (data: typeof initialData) => {
  return {
    request: {
      email: data.email,
      first_name: data.firstname,
      house_id: props.house.id,
      inquiry_type: 'house_inquiry',
      last_name: data.lastname,
      matterport_request: true,
      phone: data.phone,
    },
    newsletter: data.newsletter || false,
    hubspotutk: $httpCookies?.get('hubspotutk'),
  }
}

const mutation: MutationType<
  typeof initialData,
  ReturnType<typeof formattedPayload>
> = (data) => ({
  request: async (formData) => $api.v1.requests.create(formData),
  input: formattedPayload(data),
})

const onSubmit = () => ({
  success: () => {
    success.value = true

    trackEvent({
      category: 'Product page',
      event: 'virtual_visit_request_sent',
      house_id: Number(props.house.id),
    })
  },
  error: () => {
    success.value = false
  },
})
</script>

<template>
  <BaseModalV2
    :model-value="modelValue"
    :size="success ? 'm' : 'l'"
    :class="['lc-modal-virtual-visit', { success: success }]"
    :mobile-full-height="true"
    @update:model-value="$emit('update:model-value', $event)"
  >
    <template #content>
      <template v-if="!success">
        <h2
          :class="[
            'm-0 mb-4 text-left font-sansSerif text-lg !font-bold !leading-10 md:!leading-8',
            { 'mt-10': !$device.isMobile },
          ]"
        >
          {{ $t('house.virtualVisit.getContacted') }}
        </h2>
      </template>
      <div v-if="!success" class="flex flex-col md:flex-row-reverse">
        <div class="mb-6 w-full md:mb-0 md:ml-12 md:w-1/2">
          <p class="md:text-center">
            {{ $t('house.virtualVisit.discover') }}
          </p>
          <video autoplay class="w-full" controls loop muted playsinline>
            <source :src="matterportVideoUrl" type="video/mp4" />
          </video>
        </div>

        <base-form
          class="w-full pb-24 md:w-1/2 md:pb-0"
          :active-modal-errors="false"
          :active-modal-save-or-quit="false"
          :button-primary="btnPrimary"
          :disabled-primary-button="disabledButton"
          :group-fields="groupFieldsForm"
          :initial-data="initialData"
          :mutation="mutation"
          :on-submit="onSubmit"
          container-button-class="w-full"
          id-form="visit-virtual-form"
        />
      </div>
      <div v-else>
        <div class="mt-10 text-center">
          <BaseIcon
            class="m-auto mb-3 !text-[2.25rem] text-secondary-400"
            name="checkCircle"
          />

          <h5 class="text-xl">
            {{ $t('house.virtualVisit.success.title') }}
          </h5>

          <p class="m-0 p-6">
            {{ $t('house.virtualVisit.success.text') }}
          </p>
        </div>
      </div>
    </template>
  </BaseModalV2>
</template>

<style>
.lc-modal-virtual-visit .lc-modal__close-button.close-button {
  @apply border border-gray-200 bg-white top-4 right-4;
  box-shadow: 1px 1px 6px rgba(0, 0, 0, 0.1);
}

.lc-modal-virtual-visit .lc-modal__container--header {
  @apply shadow-none px-4 pt-4 pb-3;
}

.lc-modal-virtual-visit .lc-modal__wrap {
  @apply max-w-full;
}

.lc-modal-virtual-visit .lc-modal__content {
  @apply p-4;
}

.lc-modal-virtual-visit.success .lc-modal__content {
  @apply flex items-center;
}

@screen md {
  .lc-modal-virtual-visit .lc-modal__close-button.close-button {
    @apply top-5 right-5;
  }

  .lc-modal-virtual-visit .lc-modal__wrap {
    @apply max-w-[1084px] p-0;
  }
  .lc-modal-virtual-visit.success .lc-modal__wrap {
    @apply max-w-[600px];
  }
  .lc-modal-virtual-visit.success .lc-modal__content {
    @apply pt-12;
  }

  .lc-modal-virtual-visit .lc-modal__container--header {
    @apply px-12 pt-12 pb-0;
  }

  .lc-modal-virtual-visit .lc-modal__content {
    @apply px-12 pt-6 pb-12;
  }
}
</style>
